import * as React from "react"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreHorizontal } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

/**
 * CompletePagination - Full-featured pagination component for Teacher Portal
 * 
 * Features:
 * - Previous/Next buttons with First/Last
 * - Page number buttons with smart ellipsis
 * - Current page highlight
 * - Items-per-page selector
 * - Total items count display
 * - Keyboard navigation (Arrow keys)
 * - Responsive design
 * - Teacher Portal styling
 */

export function CompletePagination({
    currentPage = 1,
    totalPages = 1,
    onPageChange,
    itemsPerPage = 10,
    onItemsPerPageChange,
    totalItems = 0,
    showItemsPerPage = true,
    className,
    ...props
}) {
    // Generate page numbers with ellipsis
    const getPageNumbers = () => {
        const pages = []
        const showEllipsisThreshold = 7 // Show ellipsis if more than 7 pages

        if (totalPages <= showEllipsisThreshold) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i)
            }
        } else {
            // Always show first page
            pages.push(1)

            if (currentPage > 3) {
                pages.push('ellipsis-start')
            }

            // Show pages around current page
            const start = Math.max(2, currentPage - 1)
            const end = Math.min(totalPages - 1, currentPage + 1)

            for (let i = start; i <= end; i++) {
                pages.push(i)
            }

            if (currentPage < totalPages - 2) {
                pages.push('ellipsis-end')
            }

            // Always show last page
            if (totalPages > 1) {
                pages.push(totalPages)
            }
        }

        return pages
    }

    const pages = getPageNumbers()

    // Calculate displayed items range
    const startItem = (currentPage - 1) * itemsPerPage + 1
    const endItem = Math.min(currentPage * itemsPerPage, totalItems)

    // Handle keyboard navigation
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
                return // Don't interfere with form inputs
            }

            if (e.key === 'ArrowLeft' && currentPage > 1) {
                onPageChange(currentPage - 1)
            } else if (e.key === 'ArrowRight' && currentPage < totalPages) {
                onPageChange(currentPage + 1)
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [currentPage, totalPages, onPageChange])

    if (totalPages <= 1 && !showItemsPerPage) {
        return null // Don't show pagination if only 1 page and no items-per-page selector
    }

    return (
        <div
            className={cn(
                "flex flex-col sm:flex-row items-center justify-between gap-4 py-4",
                className
            )}
            {...props}
        >
            {/* Items count and per-page selector */}
            <div className="flex items-center gap-4 text-sm text-[#6B6B6B]">
                {totalItems > 0 && (
                    <span className="font-light">
                        Showing {startItem}-{endItem} of {totalItems.toLocaleString()}
                    </span>
                )}

                {showItemsPerPage && onItemsPerPageChange && (
                    <div className="flex items-center gap-2">
                        <span className="font-light">Items per page:</span>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                            className="px-2 py-1 bg-white border border-[#E0D4BF] rounded-lg text-[#2B2B2B] font-light focus:ring-2 focus:ring-[#C9A961] focus:border-transparent"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                )}
            </div>

            {/* Page navigation */}
            {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    {/* First page button */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPageChange(1)}
                        disabled={currentPage === 1}
                        className="hidden sm:flex h-8 w-8 p-0 border-[#E0D4BF] text-[#6B6B6B] hover:bg-[#F0E8D8] disabled:opacity-50"
                        aria-label="Go to first page"
                    >
                        <ChevronsLeft className="h-4 w-4" />
                    </Button>

                    {/* Previous page button */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="h-8 w-8 p-0 border-[#E0D4BF] text-[#6B6B6B] hover:bg-[#F0E8D8] disabled:opacity-50"
                        aria-label="Go to previous page"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>

                    {/* Page number buttons */}
                    <div className="flex items-center gap-1">
                        {pages.map((page, index) => {
                            if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                                return (
                                    <div
                                        key={`ellipsis-${index}`}
                                        className="h-8 w-8 flex items-center justify-center text-[#6B6B6B]"
                                    >
                                        <MoreHorizontal className="h-4 w-4" />
                                    </div>
                                )
                            }

                            const isActive = page === currentPage

                            return (
                                <Button
                                    key={page}
                                    variant={isActive ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => onPageChange(page)}
                                    className={cn(
                                        "h-8 w-8 p-0 font-light",
                                        isActive
                                            ? "bg-[#C9A961] hover:bg-[#B8935A] text-white border-[#C9A961]"
                                            : "border-[#E0D4BF] text-[#6B6B6B] hover:bg-[#F0E8D8]"
                                    )}
                                    aria-label={`Go to page ${page}`}
                                    aria-current={isActive ? "page" : undefined}
                                >
                                    {page}
                                </Button>
                            )
                        })}
                    </div>

                    {/* Next page button */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="h-8 w-8 p-0 border-[#E0D4BF] text-[#6B6B6B] hover:bg-[#F0E8D8] disabled:opacity-50"
                        aria-label="Go to next page"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>

                    {/* Last page button */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className="hidden sm:flex h-8 w-8 p-0 border-[#E0D4BF] text-[#6B6B6B] hover:bg-[#F0E8D8] disabled:opacity-50"
                        aria-label="Go to last page"
                    >
                        <ChevronsRight className="h-4 w-4" />
                    </Button>
                </div>
            )}
        </div>
    )
}

CompletePagination.displayName = "CompletePagination"
